import { useTokens } from '@frontend/shared';
import { Spinner } from '@frontend/shared/web';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { AccessDeniedPanel } from '../components/layout/AccessDeniedPanel';
import { FEATURES } from '../constants';
import { domainsOf, isAdmin as hasAdminRole, type Domain, type RoleGrant } from '../domain/access';
import { defaultAccountConfig } from '../domain/accountConfig';
import { getHttpStatus, hasBackError } from '../lib/apiError';
import { decodeJwt, splitFullName } from '../lib/jwt';
import { endSession } from '../lib/session';
import { useAccountConfig } from '../services/api/hooks/useAccountConfig';
import { useMeAccess } from '../services/api/hooks/useMeAccess';
import type { AccountConfig, AccountFilterOutput } from '../types/extranet-api.types';

/** Identité de l'appelant, lue dans les claims du JWT (pas d'email disponible). */
export interface AccountMe {
  id: string;
  userKey: string;
  accountKey: string;
  fullName: string;
  firstName: string;
  lastName: string;
}

interface AccountContextValue {
  me: AccountMe;
  /** Rôles fonctionnels et périmètre — `GET /v1/users/me`. */
  access: RoleGrant[];
  domains: ReadonlySet<Domain>;
  isAdmin: boolean;
  /** true si l'un des rôles de l'appelant couvre ce domaine. */
  can: (domain: Domain) => boolean;
  /**
   * Dimensions de filtrage activées sur le compte, avec leurs valeurs
   * sélectionnables (`GET /v1/users/me`, v1.9.0). Vide = aucune facette
   * configurée, le header retombe alors sur la saisie libre.
   */
  filters: AccountFilterOutput[];
  config: AccountConfig;
}

const AccountContext = createContext<AccountContextValue | null>(null);

interface AccountProviderProps {
  children: ReactNode;
  /** Rendu quand `/v1/users/me` échoue pour une raison autre qu'un refus d'accès (réseau, 5xx). */
  renderError: (retry: () => void) => ReactNode;
}

/** 401, ou `INVALID_TOKEN` quel que soit le statut : la session est morte. */
const isSessionError = (err: unknown): boolean => getHttpStatus(err) === 401 || hasBackError(err, 'INVALID_TOKEN');

/**
 * Charge le contexte de l'appelant. `/v1/users/me` est **bloquant** : le JWT ne
 * porte pas les rôles `Extranet_*`, rien ne peut être dérivé avant la réponse.
 *
 * - session morte (claims indécodables, 401, `INVALID_TOKEN`) → purge, et
 *   `AuthenticatedLayout` redirige vers `/login` ;
 * - 403 (session non-extranet, membre inactif, droits révoqués) → panneau
 *   « Accès refusé » avec déconnexion, sans boucle ;
 * - accès vide → même panneau ;
 * - autre erreur → `renderError` avec un bouton « Réessayer ».
 */
export const AccountProvider = ({ children, renderError }: AccountProviderProps) => {
  const { tokens } = useTokens();
  const claims = useMemo(() => decodeJwt(tokens?.accessToken), [tokens?.accessToken]);

  const meQ = useMeAccess();
  const configQ = useAccountConfig(FEATURES.accountConfigRoute);

  const mustEndSession = claims === null || isSessionError(meQ.error);
  useEffect(() => {
    if (mustEndSession) void endSession();
  }, [mustEndSession]);

  const config = useMemo(() => configQ.data ?? (claims ? defaultAccountConfig(claims) : null), [configQ.data, claims]);

  if (mustEndSession || !claims || !config) return null;

  if (meQ.isPending) return <Spinner />;

  if (meQ.isError) {
    if (getHttpStatus(meQ.error) === 403) {
      return (
        <AccessDeniedPanel description="Votre compte n'a pas accès au portail extranet. Contactez votre administrateur Allocab." />
      );
    }
    return <>{renderError(() => void meQ.refetch())}</>;
  }

  const access = meQ.data.access ?? [];
  const domains = domainsOf(access);

  if (domains.size === 0) {
    return (
      <AccessDeniedPanel description="Aucun droit n'est associé à votre compte. Contactez votre administrateur Allocab." />
    );
  }

  const value: AccountContextValue = {
    me: {
      id: claims.id,
      userKey: claims.userKey,
      accountKey: claims.accountKey,
      fullName: claims.userFullName,
      ...splitFullName(claims.userFullName),
    },
    access,
    domains,
    isAdmin: hasAdminRole(access),
    can: domain => domains.has(domain),
    filters: meQ.data.filters ?? [],
    config,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextValue => {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error("useAccount doit être utilisé à l'intérieur d'<AccountProvider>");
  }
  return ctx;
};
